import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { LuBell, LuBellOff, LuSun, LuMoon, LuMenu, LuX } from 'react-icons/lu'
import { useAuth } from '../context/AuthContext'
import { usePushNotifications } from '../hooks/usePushNotifications'

export function NavBar() {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'dark'
  })
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isNotifPopoverOpen, setIsNotifPopoverOpen] = useState(false)
  const { user, logout, openAuthModal } = useAuth()
  const { isSupported, isSubscribed, isLoading, subscribe, unsubscribe } = usePushNotifications()

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('theme', theme)
  }, [theme])

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))
  }

  return (
    <nav className="bg-surface border-b border-border py-4 mb-8 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 flex justify-between items-center relative">
        <Link
          to="/"
          className="text-xl sm:text-2xl font-bold text-text no-underline hover:text-primary transition-colors"
        >
          Cryptic Share
        </Link>

        {/* Desktop nav */}
        <div className="hidden sm:flex items-center gap-6">
          <Link
            to="/"
            className="text-text-secondary no-underline font-medium hover:text-primary transition-colors"
          >
            Home
          </Link>
          {user?.isAdmin && (
            <Link
              to="/admin"
              className="text-text-secondary no-underline font-medium hover:text-primary transition-colors"
            >
              Admin
            </Link>
          )}
          {user ? (
            <div className="flex items-center gap-4">
              <span className="text-text-secondary">{user.username}</span>
            </div>
          ) : (
            <button
              onClick={openAuthModal}
              className="text-text-secondary no-underline font-medium hover:text-primary transition-colors bg-transparent border-none cursor-pointer"
            >
              Login/Register
            </button>
          )}
          {/* {isSupported && (
            <div className="relative">
              <button
                onClick={() => setIsNotifPopoverOpen((v) => !v)}
                className={`p-2 rounded-lg border flex items-center justify-center transition-all ${
                  isSubscribed
                    ? 'bg-primary/10 border-primary text-primary'
                    : 'bg-input-bg border-border hover:border-primary text-text-secondary'
                }`}
                aria-label="Toggle notifications"
              >
                {isSubscribed ? '🔔' : '🔕'}
              </button>
              {isNotifPopoverOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-surface border border-border rounded-xl shadow-lg p-4 z-50">
                  <div className="text-sm text-text mb-3">
                    {isSubscribed
                      ? 'Push notifications enabled. Get notified when collaborators update puzzles.'
                      : 'Enable notifications to know when collaborators update shared puzzles.'}
                  </div>
                  <button
                    onClick={async () => {
                      if (isSubscribed) {
                        await unsubscribe()
                      } else {
                        await subscribe()
                      }
                      setIsNotifPopoverOpen(false)
                    }}
                    disabled={isLoading}
                    className={`w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                      isSubscribed
                        ? 'bg-red-500/10 text-red-500 hover:bg-red-500/20'
                        : 'bg-primary text-white hover:bg-primary/90'
                    } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isLoading
                      ? 'Loading...'
                      : isSubscribed
                      ? 'Disable Notifications'
                      : 'Enable Notifications'}
                  </button>
                </div>
              )}
            </div>
          )} */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg bg-input-bg border border-border hover:border-primary flex items-center justify-center transition-all"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <LuSun size={18} /> : <LuMoon size={18} />}
          </button>
        </div>

        {/* Mobile menu button */}
        <div className="sm:hidden flex items-center">
          <button
            type="button"
            onClick={() => setIsMenuOpen((v) => !v)}
            className="p-2 rounded-lg bg-input-bg border border-border hover:border-primary flex items-center justify-center transition-all"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <LuX size={22} /> : <LuMenu size={22} />}
          </button>
        </div>

        {/* Mobile popout */}
        {isMenuOpen && (
          <div className="sm:hidden absolute right-4 top-full mt-3 w-56 bg-surface border border-border rounded-xl shadow-lg overflow-hidden">
            <div className="flex flex-col">
              <Link
                to="/"
                onClick={() => setIsMenuOpen(false)}
                className="px-4 py-3 text-text no-underline hover:bg-input-bg transition-colors"
              >
                Home
              </Link>
              {user?.isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setIsMenuOpen(false)}
                  className="px-4 py-3 text-text no-underline hover:bg-input-bg transition-colors"
                >
                  Admin
                </Link>
              )}
              <div className="border-t border-border" />
              {user ? (
                <div className="px-4 py-3 flex flex-col gap-2">
                  <span className="text-sm text-text-secondary">{user.username}</span>
                  <button
                    onClick={() => {
                      logout()
                      setIsMenuOpen(false)
                    }}
                    className="text-left text-text-secondary hover:text-red-500 transition-colors"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    openAuthModal()
                    setIsMenuOpen(false)
                  }}
                  className="w-full text-left px-4 py-3 text-text no-underline hover:bg-input-bg transition-colors bg-transparent border-none cursor-pointer"
                >
                  Login
                </button>
              )}
              <div className="border-t border-border" />
              {isSupported && (
                <button
                  onClick={async () => {
                    if (isSubscribed) {
                      await unsubscribe()
                    } else {
                      await subscribe()
                    }
                    setIsMenuOpen(false)
                  }}
                  disabled={isLoading}
                  className="px-4 py-3 text-left text-text hover:bg-input-bg transition-colors flex items-center gap-2"
                >
                {isSubscribed ? <LuBell size={18} /> : <LuBellOff size={18} />}
                  <span>
                    {isLoading
                      ? 'Loading...'
                      : isSubscribed
                        ? 'Disable Notifications'
                        : 'Enable Notifications'}
                  </span>
                </button>
              )}
              <button
                onClick={() => {
                  toggleTheme()
                  setIsMenuOpen(false)
                }}
                className="px-4 py-3 text-left text-text hover:bg-input-bg transition-colors"
                aria-label="Toggle theme"
              >
                Theme {theme === 'dark' ? <LuSun size={16} className="inline" /> : <LuMoon size={16} className="inline" />}
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}
